// Command palette — modal UI mounted once globally at the v2 dashboard root.
//
// Per .forge/plans/v2-command-palette.md.
// Visual reference: https://tmp.aloragiving.com/05-command-palette.html
// (Adapted to v2 tokens defined in apps/dashboard-v2/src/styles/tokens.css.)

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import {
  GROUP_LABELS,
  type CommandEntry,
  isCommandForwardPromised,
  navItemsForLookup,
  type NavLookupItem,
} from "./registry";
import { usePalette } from "./usePalette";
import { defaultCommands } from "./commands";
import { navGroups } from "../shell/nav";
import { listDonors } from "../../api/donors";

export interface CommandPaletteProps {
  // Optional override for tests / Storybook so we can drive the palette
  // without wiring react-router. Production callers should not set this.
  entries?: CommandEntry[];
  onNavigate?: (route: string) => void;
}

type LucideMap = Record<string, ComponentType<{ size?: number; "aria-hidden"?: boolean | "true" | "false" }>>;

function IconFor({ name, size = 16 }: { name?: string; size?: number }) {
  if (!name) return null;
  // Convert "layout-dashboard" → "LayoutDashboard"
  const pascal = name
    .split("-")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
    .join("");
  const Cmp = (LucideIcons as unknown as LucideMap)[pascal];
  if (!Cmp) return null;
  return <Cmp size={size} aria-hidden="true" />;
}

export function CommandPalette({
  entries = defaultCommands,
  onNavigate,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const handleNavigate = useCallback(
    (route: string) => {
      if (onNavigate) {
        onNavigate(route);
      } else {
        navigate(route);
      }
    },
    [navigate, onNavigate],
  );

  // CHG-1662 / ITER-3771 — live nav lookup feeds isCommandForwardPromised.
  // Computed once on mount; nav.ts is a module-level literal so this is
  // a stable snapshot per process (matches the donor's actual rendered
  // sidebar). Live re-evaluation would require subscribing to a
  // nav-state store, which doesn't exist yet — the static snapshot is
  // already a strict improvement over commands.tsx's bake-time flag.
  const navItemsLookup: NavLookupItem[] = useMemo(
    () => navItemsForLookup(navGroups),
    [],
  );

  // CHG-1662 / ITER-3771 — live donor search. The bake-time
  // "Sample donor — Alice Park" rows in commands.tsx were removed
  // (they leaked into search results because the palette's substring
  // scorer matched their hard-coded keywords on any "al" / "br" /
  // "ca" query). Real donor matches now come from
  // /api/v1/donors?search=q via the listDonors fetcher; populated
  // when the user types ≥2 chars; debounced 200ms; capped at 8
  // results so the donor section doesn't dominate the list.
  const [donorEntries, setDonorEntries] = useState<CommandEntry[]>([]);
  const augmentedEntries = useMemo<CommandEntry[]>(
    () => [...entries, ...donorEntries],
    [entries, donorEntries],
  );

  const {
    open,
    setOpen,
    query,
    setQuery,
    grouped,
    flat,
    highlightIndex,
    setHighlightIndex,
    select,
    notice,
  } = usePalette({ entries: augmentedEntries, onNavigate: handleNavigate });

  // Debounced donor search. Cleared on empty query or short query.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setDonorEntries([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const result = await listDonors({ search: q, pageSize: 8 });
        if (cancelled) return;
        const next: CommandEntry[] = result.items.map((d) => ({
          id: `donor-${d.id}`,
          label: d.email ? `${d.full_name} — ${d.email}` : d.full_name,
          group: "donors" as const,
          icon: "user",
          route: `/donors/${d.id}`,
          // Empty keywords — we want substring matches against label only
          // so a query like "ali" doesn't pull in unrelated donor rows.
          // The scorer in registry.ts already matches label.includes(q).
          keywords: [],
        }));
        setDonorEntries(next);
      } catch {
        if (cancelled) return;
        // On fetch error, surface no donor rows rather than a stale
        // result — better to be quiet than show stale data.
        setDonorEntries([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);

  // Autofocus the search input on open; restore focus on close.
  useEffect(() => {
    if (open) {
      lastFocusRef.current = document.activeElement as HTMLElement | null;
      // Defer one tick so the input is mounted.
      const id = requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    } else {
      const last = lastFocusRef.current;
      if (last && typeof last.focus === "function") {
        last.focus();
      }
    }
  }, [open]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex(Math.min(flat.length - 1, highlightIndex + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex(Math.max(0, highlightIndex - 1));
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        setHighlightIndex(0);
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        setHighlightIndex(Math.max(0, flat.length - 1));
        return;
      }
      if (e.key === "PageDown") {
        e.preventDefault();
        setHighlightIndex(Math.min(flat.length - 1, highlightIndex + 8));
        return;
      }
      if (e.key === "PageUp") {
        e.preventDefault();
        setHighlightIndex(Math.max(0, highlightIndex - 8));
        return;
      }
      if (e.key === "Tab") {
        // Focus trap: keep focus inside the palette. The combobox pattern
        // navigates options via aria-activedescendant + arrow keys, not Tab,
        // so we keep Tab as a no-op that holds focus on the input.
        e.preventDefault();
        inputRef.current?.focus();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const target = flat[highlightIndex];
        if (target) select(target);
        return;
      }
    },
    [flat, highlightIndex, select, setHighlightIndex, setOpen],
  );

  if (!open) return null;

  // Build a stable flat row index alongside the grouped render so hover
  // and keyboard highlight refer to the same coordinate space.
  let rowCounter = -1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onKeyDown={onKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        // composition-lint-exempt: modal/overlay backdrop scrim — pending shared --color-scrim token (DRIFT-776)
        background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
      }}
    >
      <div
        style={{
          width: 560,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "70vh",
          background: "var(--color-surface)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-md, 12px)",
          boxShadow: "var(--shadow-lg, 0 24px 48px rgba(15,23,42,0.18))",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Search input */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            borderBottom: "1px solid var(--color-border-subtle)",
          }}
        >
          <IconFor name="search" size={16} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, actions, donors…"
            role="combobox"
            aria-expanded={flat.length > 0}
            aria-controls="command-palette-listbox"
            aria-activedescendant={
              flat[highlightIndex]
                ? `command-palette-row-${flat[highlightIndex].id}`
                : undefined
            }
            aria-autocomplete="list"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 15,
              background: "transparent",
              color: "var(--color-text)",
            }}
          />
        </div>

        {/* Results list */}
        <div
          id="command-palette-listbox"
          role="listbox"
          aria-label="Command results"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "8px 0",
          }}
        >
          {flat.length === 0 && (
            <div
              style={{
                padding: "32px 16px",
                textAlign: "center",
                color: "var(--color-text-muted)",
                fontSize: 13,
              }}
            >
              No matches for &ldquo;{query}&rdquo;
            </div>
          )}

          {grouped.map(({ group, entries: groupEntries }) => (
            <div key={group}>
              <div
                style={{
                  padding: "6px 16px",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--color-text-muted)",
                  fontWeight: 500,
                }}
              >
                {GROUP_LABELS[group]}
              </div>
              {groupEntries.map((entry) => {
                rowCounter += 1;
                const rowIdx = rowCounter;
                const highlighted = rowIdx === highlightIndex;
                return (
                  <button
                    key={`${group}-${entry.id}`}
                    id={`command-palette-row-${entry.id}`}
                    role="option"
                    aria-selected={highlighted}
                    onMouseEnter={() => setHighlightIndex(rowIdx)}
                    onClick={() => select(entry)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      padding: "8px 16px",
                      border: "none",
                      textAlign: "left",
                      cursor: "pointer",
                      background: highlighted
                        ? "var(--color-surface-hover)"
                        : "transparent",
                      color: "var(--color-text)",
                      fontSize: 13.5,
                      fontFamily: "inherit",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 20,
                        height: 20,
                        color: "var(--color-text-muted)",
                        flexShrink: 0,
                      }}
                    >
                      <IconFor name={entry.icon} size={16} />
                    </span>
                    <span style={{ flex: 1 }}>{entry.label}</span>
                    {isCommandForwardPromised(entry, navItemsLookup) && (
                      <span
                        style={{
                          fontSize: 10,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: "var(--color-text-muted)",
                          border: "1px solid var(--color-border-subtle)",
                          borderRadius: 4,
                          padding: "1px 6px",
                          flexShrink: 0,
                        }}
                      >
                        Soon
                      </span>
                    )}
                    {entry.shortcut && (
                      <kbd
                        style={{
                          fontSize: 10.5,
                          fontFamily: "ui-monospace, monospace",
                          color: "var(--color-text-muted)",
                          background: "var(--color-bg-subtle)",
                          padding: "1px 6px",
                          borderRadius: 4,
                          flexShrink: 0,
                        }}
                      >
                        {entry.shortcut}
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 16px",
            borderTop: "1px solid var(--color-border-subtle)",
            background: "var(--color-bg)",
            fontSize: 11,
            color: "var(--color-text-muted)",
            minHeight: 32,
          }}
        >
          {notice ? (
            <span role="status" aria-live="polite">
              {notice}
            </span>
          ) : (
            <>
              <FooterChip label="↑ ↓" hint="Navigate" />
              <FooterChip label="↵" hint="Select" />
              <FooterChip label="Esc" hint="Close" />
              <span style={{ flex: 1 }} />
              <FooterChip label="⌘K" hint="Toggle" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FooterChip({ label, hint }: { label: string; hint: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <kbd
        style={{
          fontFamily: "ui-monospace, monospace",
          padding: "1px 6px",
          background: "var(--color-surface)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: 4,
        }}
      >
        {label}
      </kbd>
      <span>{hint}</span>
    </span>
  );
}
